/**
 * The checkout's flight recorder.
 *
 * ## Why this exists
 *
 * The owner's live box produced a stall that left no evidence anywhere: the
 * card frame tokenized, the next server call never fired, the console was
 * clean, and nothing on the page said anything. A stall that cannot be
 * explained after the fact will happen again, so the checkout now keeps the
 * facts of its own run — timestamps, state changes, message events, API calls,
 * timers — in a ring buffer that a "copy diagnostics" button can hand to a
 * person.
 *
 * ## The rule that makes it safe
 *
 * **The recorder has nowhere to put a secret.** Every event is one of a closed
 * set of shapes, and none of them has a field for a token, a card number, the
 * transient token, billing data or an attempt nonce: values are recorded only
 * as *lengths* (how long a call took, how big a value was). A `postMessage` is
 * recorded as its origin and its type and nothing else — the payload is never
 * passed to this module at all. The type system is the redaction: there is no
 * string field a secret could ride on.
 *
 * ## When it runs
 *
 * Off unless `?debug=1` is in the URL or `qalamDebug` is set in
 * `localStorage`. Recording is a no-op otherwise — the buffer is not filled,
 * nothing is stringified, and a donor's normal checkout carries none of this.
 */

export interface DebugEvent {
  /** Milliseconds since the recorder started. */
  at: number;
  /** What happened, from the closed set the callers use. */
  kind: string;
  /** Metadata only. The recorder's callers cannot pass a value here — see the event helpers. */
  detail?: string;
}

const BUFFER_LIMIT = 200;

let events: DebugEvent[] = [];
let startedAt = 0;

/** Whether recording is on: `?debug=1` or `localStorage.qalamDebug`. */
export function debugEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if (new URLSearchParams(window.location.search).has('debug')) return true;
    return window.localStorage.getItem('qalamDebug') === '1';
  } catch {
    return false;
  }
}

/** Start (or restart) the clock. Called once per checkout run when enabled. */
export function debugStart(): void {
  startedAt = Date.now();
  events = [];
}

function record(kind: string, detail?: string): void {
  if (!debugEnabled()) return;
  if (events.length === 0) startedAt = Date.now();
  events.push({ at: Date.now() - startedAt, kind, ...(detail !== undefined ? { detail } : {}) });
  if (events.length > BUFFER_LIMIT) events = events.slice(-BUFFER_LIMIT);
}

/** A checkout stage changed. The stage name is a closed vocabulary, not user data. */
export function debugStage(stage: string): void {
  record('stage', stage);
}

/**
 * One API call: path, status, duration. Never a body, never a query string.
 *
 * A resolved call records `200` - the recorder's one word for "answered",
 * because `apiFetch` hands back parsed bodies, not statuses. A thrown call
 * records the real status it was refused with, which is the one that matters
 * in a diagnosis.
 */
export function debugApi(path: string, status: number | null, durationMs: number): void {
  const trimmedPath = path.split('?')[0] ?? path;
  record('api', `${trimmedPath} -> ${status ?? 'network_error'} in ${Math.round(durationMs)}ms`);
}

/** One `window` message event: origin and type only. Never the payload. */
export function debugMessage(origin: string, type: string | null): void {
  record('message', `origin=${origin} type=${type ?? '(unparsed)'}`);
}

/** A timer: started, fired, or cleared. */
export function debugTimer(
  name: 'show' | 'accept' | 'inject' | 'ddc' | 'watchdog' | 'still-waiting',
  what: 'started' | 'fired' | 'cleared',
): void {
  record('timer', `${name} ${what}`);
}

/** The card-frame lifecycle: `show()` and the library calls around it. */
export function debugFrame(
  what:
    | 'inject-start'
    | 'inject-done'
    | 'inject-blocked'
    | 'accept-start'
    | 'accept-ready'
    | 'accept-refused'
    | 'show-start'
    | 'show-token'
    | 'show-timeout'
    | 'show-refused'
    | 'show-empty',
): void {
  record('frame', what);
}

/** Everything recorded, oldest first, as the text a person pastes into a ticket. */
export function debugDump(): string {
  const lines = events.map((e) => `+${e.at}ms ${e.kind}${e.detail ? ` ${e.detail}` : ''}`);
  return `qalam checkout diagnostics\n${lines.join('\n')}\n`;
}

/** For tests: everything recorded, structured. */
export function debugEvents(): readonly DebugEvent[] {
  return events;
}

/** For tests: clear without touching the enabled state. */
export function debugReset(): void {
  events = [];
  startedAt = Date.now();
}
